import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  File,
  Code,
  Loader2,
  ChevronDown,
  ExternalLink,
  FolderOpen,
  LayoutGrid,
  List,
  MoreHorizontal,
  Eye,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '../api/coworkApi';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────

interface FileEntry {
  id: string;
  name: string;
  type: 'pptx' | 'docx' | 'xlsx' | 'pdf' | 'image' | 'html' | 'other';
  url: string;
  size?: number;
  agentId?: string;
  agentName?: string;
  source: 'task' | 'skill' | 'chat' | 'upload';
  taskId?: string;
  taskTitle?: string;
  createdAt: string;
}

type FilterType = 'all' | 'documents' | 'images' | 'pdf';
type SortMode = 'newest' | 'oldest' | 'name';
type ViewMode = 'grid' | 'list';

// ─────────────────────────────────────────────
//  Agent map
// ─────────────────────────────────────────────

const AGENT_MAP: Record<string, { name: string; emoji: string; color: string }> = {
  'account-manager': { name: 'Lykke', emoji: '\u{1F4C1}', color: '#2e024c' },
  'maya-seo':        { name: 'Joanya', emoji: '\u{1F50D}', color: '#00b1a5' },
  'leo-perf':        { name: 'Lena', emoji: '\u{1F4C8}', color: '#be43ff' },
  'aria-content':    { name: 'Aria', emoji: '\u2728', color: '#2e024c' },
  'rex-influencer':  { name: 'Rex', emoji: '\u{1F3AD}', color: '#00b1a5' },
  'nova-strategy':   { name: 'Nova', emoji: '\u{1F4A1}', color: '#be43ff' },
  'kai-analytics':   { name: 'Kai', emoji: '\u{1F4CA}', color: '#00b1a5' },
  'jones-artdir':    { name: 'Jones', emoji: '\u{1F3A8}', color: '#be43ff' },
};

function agentInfo(id?: string) {
  if (!id) return null;
  return AGENT_MAP[id] ?? { name: id, emoji: '\u{1F916}', color: '#6b7280' };
}

// ─────────────────────────────────────────────
//  File type config
// ─────────────────────────────────────────────

const FILE_TYPE_CONFIG: Record<string, { icon: typeof FileText; color: string; bg: string; gradient: string; label: string }> = {
  pptx:  { icon: FileSpreadsheet, color: '#ff5635', bg: '#fff4f2', gradient: 'from-[#ff5635]/10 to-[#ff8c6b]/10', label: 'PPTX' },
  docx:  { icon: FileText,        color: '#2e024c', bg: '#f5f0ff', gradient: 'from-[#2e024c]/10 to-[#6b4fbb]/10', label: 'DOCX' },
  xlsx:  { icon: FileSpreadsheet, color: '#00b1a5', bg: '#f0fdf4', gradient: 'from-[#00b1a5]/10 to-[#4dd0c8]/10', label: 'XLSX' },
  pdf:   { icon: FileText,        color: '#be43ff', bg: '#f5f0ff', gradient: 'from-[#be43ff]/10 to-[#d87bff]/10', label: 'PDF' },
  image: { icon: ImageIcon,       color: '#00b1a5', bg: '#f0fdf4', gradient: 'from-[#00b1a5]/10 to-[#4dd0c8]/10', label: 'Image' },
  html:  { icon: Code,            color: '#a9f600', bg: '#f7ffe6', gradient: 'from-[#a9f600]/10 to-[#c8ff4d]/10', label: 'HTML' },
  other: { icon: File,            color: '#6b7280', bg: '#f9fafb', gradient: 'from-[#6b7280]/10 to-[#9ca3af]/10', label: 'File' },
};

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
}

function formatSize(bytes?: number): string | null {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function matchesTypeFilter(type: string, filter: FilterType): boolean {
  if (filter === 'all') return true;
  if (filter === 'documents') return type === 'pptx' || type === 'docx' || type === 'xlsx';
  if (filter === 'images') return type === 'image';
  if (filter === 'pdf') return type === 'pdf';
  return true;
}

function isPreviewable(file: FileEntry): boolean {
  return file.type === 'image' || file.type === 'html';
}

function getPreviewUrl(file: FileEntry): string | null {
  if (file.type === 'image') {
    // For images, use the URL directly as thumbnail
    if (file.url.startsWith('http')) return file.url;
    const apiBase = (import.meta as any).env?.VITE_COWORK_API_URL || '';
    return `${apiBase}${file.url}`;
  }
  return null;
}

// ─────────────────────────────────────────────
//  Filter tabs (like the reference design)
// ─────────────────────────────────────────────

const TAB_FILTERS: { key: FilterType; label: string; icon?: typeof File }[] = [
  { key: 'all',       label: 'All',       icon: FolderOpen },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'images',    label: 'Images',    icon: ImageIcon },
  { key: 'pdf',       label: 'PDFs',      icon: FileText },
];

// ─────────────────────────────────────────────
//  Grid File Card (thumbnail + title)
// ─────────────────────────────────────────────

function GridFileCard({ file, onClick }: { file: FileEntry; onClick: () => void }) {
  const config = FILE_TYPE_CONFIG[file.type] || FILE_TYPE_CONFIG.other;
  const Icon = config.icon;
  const previewUrl = getPreviewUrl(file);
  const agent = agentInfo(file.agentId);

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-xl border border-[#e5e7eb] bg-white overflow-hidden hover:shadow-lg hover:border-[#d0d5dd] transition-all duration-200"
    >
      {/* Thumbnail area */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-[#f9fafb]">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        {/* Fallback: colored icon card */}
        <div className={cn(
          'absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br',
          config.gradient,
          previewUrl ? 'hidden' : ''
        )}>
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2"
            style={{ backgroundColor: `${config.color}15` }}
          >
            <Icon size={28} style={{ color: config.color }} />
          </div>
          <span
            className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
            style={{ color: config.color, backgroundColor: `${config.color}15` }}
          >
            {config.label}
          </span>
        </div>

        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex gap-2">
            <a
              href={file.url.startsWith('http') ? file.url : `${(import.meta as any).env?.VITE_COWORK_API_URL || ''}${file.url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#344054] hover:bg-white transition-all shadow-sm"
              title="Preview"
            >
              <Eye size={16} />
            </a>
            <a
              href={file.url.startsWith('http') ? file.url : `${(import.meta as any).env?.VITE_COWORK_API_URL || ''}${file.url}`}
              download
              onClick={(e) => e.stopPropagation()}
              className="w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#344054] hover:bg-white transition-all shadow-sm"
              title="Download"
            >
              <Download size={16} />
            </a>
          </div>
        </div>

        {/* Agent badge (top-right) */}
        {agent && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur rounded-full pl-1 pr-2 py-0.5 text-[10px] font-medium shadow-sm"
            style={{ color: agent.color }}
          >
            <span style={{ fontSize: 12 }}>{agent.emoji}</span>
            {file.agentName || agent.name}
          </div>
        )}
      </div>

      {/* Bottom info */}
      <div className="px-3 py-2.5">
        <p className="text-[13px] font-medium text-[#1d2939] truncate leading-tight">
          {file.name}
        </p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[11px] text-[#98a2b3]">
            {timeAgo(file.createdAt)}
          </span>
          {file.size && (
            <>
              <span className="text-[#d0d5dd]">·</span>
              <span className="text-[11px] text-[#98a2b3]">{formatSize(file.size)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  List File Card (compact row)
// ─────────────────────────────────────────────

function ListFileCard({ file }: { file: FileEntry }) {
  const config = FILE_TYPE_CONFIG[file.type] || FILE_TYPE_CONFIG.other;
  const Icon = config.icon;
  const agent = agentInfo(file.agentId);
  const previewUrl = getPreviewUrl(file);
  const fileUrl = file.url.startsWith('http') ? file.url : `${(import.meta as any).env?.VITE_COWORK_API_URL || ''}${file.url}`;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border border-[#e5e7eb] rounded-xl hover:shadow-sm transition-all group">
      {/* Thumbnail / icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden" style={{ backgroundColor: config.bg }}>
        {previewUrl ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={18} style={{ color: config.color }} />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[#1d2939] truncate">{file.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span
            className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
            style={{ color: config.color, backgroundColor: config.bg }}
          >
            {config.label}
          </span>
          {agent && (
            <span className="text-[11px] font-medium" style={{ color: agent.color }}>
              {agent.emoji} {file.agentName || agent.name}
            </span>
          )}
          <span className="text-[11px] text-[#98a2b3]">{timeAgo(file.createdAt)}</span>
          {file.size && <span className="text-[11px] text-[#98a2b3]">{formatSize(file.size)}</span>}
        </div>
      </div>

      {/* Actions */}
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-[#f2f4f7] transition-all opacity-0 group-hover:opacity-100"
      >
        <Download size={15} />
      </a>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Preview modal
// ─────────────────────────────────────────────

function PreviewModal({ file, onClose }: { file: FileEntry; onClose: () => void }) {
  const fileUrl = file.url.startsWith('http') ? file.url : `${(import.meta as any).env?.VITE_COWORK_API_URL || ''}${file.url}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-8" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[80vh] w-full bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e7eb]">
          <p className="text-[14px] font-medium text-[#1d2939] truncate">{file.name}</p>
          <div className="flex items-center gap-2">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] font-medium text-[#667085] hover:text-[#344054] flex items-center gap-1"
            >
              <ExternalLink size={13} /> Open
            </a>
            <a
              href={fileUrl}
              download
              className="text-[12px] font-medium text-[#667085] hover:text-[#344054] flex items-center gap-1"
            >
              <Download size={13} /> Download
            </a>
            <button onClick={onClose} className="ml-2 text-[#98a2b3] hover:text-[#344054] text-lg">×</button>
          </div>
        </div>
        <div className="flex items-center justify-center p-4 max-h-[70vh] overflow-auto bg-[#f9fafb]">
          {file.type === 'image' ? (
            <img src={fileUrl} alt={file.name} className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-sm" />
          ) : file.type === 'html' ? (
            <iframe src={fileUrl} className="w-full h-[65vh] rounded-lg border border-[#e5e7eb]" title={file.name} />
          ) : file.type === 'pdf' ? (
            <iframe src={fileUrl} className="w-full h-[65vh] rounded-lg border border-[#e5e7eb]" title={file.name} />
          ) : (
            <div className="text-center py-20 text-[#98a2b3]">
              <File size={48} className="mx-auto mb-3 opacity-50" />
              <p className="text-[14px]">Preview not available for this file type</p>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#2563eb] hover:underline mt-2 inline-block">
                Open in new tab →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────

export function FilesPage({ brandId, onBack }: { brandId?: string; onBack: () => void }) {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [previewFile, setPreviewFile] = useState<FileEntry | null>(null);

  // ── Fetch files ──
  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params: { agentId?: string; brandId?: string } = {};
      if (agentFilter !== 'all') params.agentId = agentFilter;
      if (brandId) params.brandId = brandId;
      const { files: data } = await coworkApi.files.list(params);
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [agentFilter, brandId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // ── Client-side type filter + sort ──
  const filtered = files
    .filter(f => matchesTypeFilter(f.type, typeFilter))
    .sort((a, b) => {
      if (sortMode === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortMode === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="flex flex-col h-full bg-[#f9fafb]">
      {/* ── Header ── */}
      <div className="flex-shrink-0 border-b border-[#f2f4f7] bg-white">
        <div className="max-w-[1200px] mx-auto w-full px-6 py-4">
          {/* Back + title */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-[#f2f4f7] text-[#667085] hover:text-[#344054] transition-colors"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
              <FolderOpen size={20} className="text-[#00b1a5]" />
              <h1 className="text-[18px] font-semibold text-[#1d2939]">Files</h1>
            </div>
          </div>

          {/* Filter tabs + controls */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Left: type filter tabs */}
            <div className="flex items-center gap-1 bg-[#f2f4f7] rounded-lg p-0.5">
              {TAB_FILTERS.map(tab => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setTypeFilter(tab.key)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium transition-all',
                      typeFilter === tab.key
                        ? 'bg-white text-[#1d2939] shadow-sm'
                        : 'text-[#667085] hover:text-[#344054]'
                    )}
                  >
                    {TabIcon && <TabIcon size={14} />}
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Right: sort + view toggle */}
            <div className="flex items-center gap-2">
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value as SortMode)}
                className="appearance-none bg-white border border-[#e5e7eb] rounded-lg pl-3 pr-8 py-1.5 text-[13px] font-medium text-[#344054] cursor-pointer hover:border-[#d0d5dd] focus:outline-none"
              >
                <option value="newest">Last edited</option>
                <option value="oldest">Oldest</option>
                <option value="name">Name A-Z</option>
              </select>

              {/* View toggle */}
              <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    'p-1.5 transition-all',
                    viewMode === 'grid' ? 'bg-[#2563eb] text-white' : 'bg-white text-[#667085] hover:text-[#344054]'
                  )}
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'p-1.5 transition-all',
                    viewMode === 'list' ? 'bg-[#2563eb] text-white' : 'bg-white text-[#667085] hover:text-[#344054]'
                  )}
                >
                  <List size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto w-full px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#98a2b3]">
              <Loader2 size={28} className="animate-spin mb-3" />
              <span className="text-[13px]">Loading files...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 rounded-full bg-[#f0fdf4] flex items-center justify-center mb-4">
                <FolderOpen size={28} className="text-[#00b1a5]" />
              </div>
              <p className="text-[15px] font-medium text-[#344054] mb-1">No files yet</p>
              <p className="text-[13px] text-[#98a2b3] max-w-[320px]">
                Files generated by agents will appear here.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filtered.map(f => (
                <GridFileCard key={f.id} file={f} onClick={() => setPreviewFile(f)} />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(f => (
                <ListFileCard key={f.id} file={f} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Preview modal ── */}
      {previewFile && (
        <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
}
